/**
 * The icon layer — D80, decided 12 August 2026.
 *
 * The library is **Lucide** (ISC, with portions MIT from Feather). It is the
 * only candidate that can SATISFY the iconography rule rather than approximate
 * it: its icons ship as `viewBox="0 0 24 24"` with `fill="none"` and
 * `stroke="currentColor"`, so the 24px grid, the no-fill rule and colour
 * inheritance are already correct. The three things that differ —
 * `stroke-width="2"`, `stroke-linecap="round"`, `stroke-linejoin="round"` —
 * are **presentation attributes**, which any CSS rule outranks. That is the
 * whole adaptation, and it lives in the theme once (`MuiSvgIcon` overrides in
 * `../theme/index.ts`) rather than at 244 call sites.
 *
 * Phosphor was rejected on exactly this point: its icons are filled paths on a
 * 256 grid with terminals baked into the geometry, so no CSS can reach them and
 * adopting it would have meant dropping the rule. `@mui/icons-material`, which
 * this replaces, fails the same way — Material icons are filled paths.
 *
 * WHY EACH GLYPH IS WRAPPED IN MUI'S `SvgIcon`
 *
 * 244 call sites pass `fontSize="small"`, `color="error"`, `sx={{…}}` and
 * `titleAccess`. Wrapping in `SvgIcon` keeps every one of those working, so the
 * migration is a rename rather than a rewrite — and `inheritViewBox` keeps
 * Lucide's own viewBox, so the geometry is untouched. The rendered element
 * carries both class sets (`lucide lucide-plus MuiSvgIcon-root …`), which is
 * what lets the theme reach it.
 *
 * The one thing to know: `.MuiSvgIcon-root` sets `fill: currentColor` in CSS,
 * and CSS beats Lucide's `fill="none"` presentation attribute. Without the
 * theme override every icon would render as a filled blob. The override is not
 * cosmetic polish — it is load-bearing.
 *
 * Sizes follow MUI's scale (`small` 20 / `medium` 24 / `large` 35), which is
 * the six-step scale the guide asks for expressed in the props the codebase
 * already uses.
 *
 * IMPORTED FROM `@aeriosiq/ui/icons`, NOT THE ROOT BARREL. `packages/ui`
 * already exports a component called `InfoIcon` — a tooltip-fronted help
 * affordance, not a glyph — and re-exporting Lucide's `Info` under the same
 * name from the same barrel would shadow it. A subpath keeps the two apart and
 * makes an icon import visually distinct from a component import, which is
 * worth having on its own.
 */
import {
  SvgIcon,
  type SvgIconProps,
  type SxProps,
  type Theme,
} from "@mui/material";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Ban,
  Bell,
  BookOpen,
  Box,
  CalendarDays,
  Camera,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CircleMinus,
  ClipboardList,
  Columns3,
  Copy,
  Download,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  FileUp,
  Folder,
  GripVertical,
  History,
  House,
  Inbox,
  Info,
  KeyRound,
  LayoutGrid,
  Link,
  LocateFixed,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Monitor,
  NotebookPen,
  Pencil,
  Pentagon,
  Pin,
  Plus,
  Redo2,
  RefreshCw,
  RotateCcw,
  Rows2,
  Rows3,
  Save,
  ScanSearch,
  Search,
  Globe,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  SlidersVertical,
  Square,
  Star,
  Trash2,
  TriangleAlert,
  Umbrella,
  Undo2,
  Unlink,
  User,
  UserPlus,
  Users,
  Wind,
  Wrench,
  X,
  Bug,
  Building2,
  Library,
  ReceiptText,
  ScrollText,
  Timer,
  Pause,
} from "lucide-react";

export interface IconProps extends SvgIconProps {
  /**
   * Fills the glyph instead of stroking it — the ON half of a binary state.
   *
   * Material shipped filled and outlined variants of the same glyph, and three
   * places in this codebase used that pair to carry meaning: pinned vs
   * unpinned, favourited vs not. Lucide has ONE glyph per concept and strokes
   * it, so the migration would have collapsed those pairs into two identical
   * icons and quietly deleted the state.
   *
   * Filling is the Lucide idiom for exactly this, so the pair survives as
   * fill-vs-stroke rather than as two drawings. It stays a *second* signal
   * alongside the label and `aria-pressed` — D26 makes colour-or-glyph-alone a
   * defect, and that applies just as much to a state you can only infer.
   */
  filled?: boolean;
}

/**
 * Wraps a Lucide glyph so it accepts every prop an `@mui/icons-material` icon
 * accepted. `inheritViewBox` keeps Lucide's `0 0 24 24` rather than letting
 * `SvgIcon` impose its own.
 *
 * The `&&.lucide` selector on the filled variant is deliberate: the theme's own
 * rule is `.<generated>.lucide` at specificity 0-2-0, so a plain `sx` fill at
 * 0-1-0 would lose to it and silently do nothing. Doubling the ampersand takes
 * it to 0-3-0 and settles the question without depending on injection order.
 */
function icon(Glyph: LucideIcon, displayName: string) {
  function Icon({ filled, sx, ...props }: IconProps) {
    const incoming: SxProps<Theme>[] = Array.isArray(sx)
      ? (sx as SxProps<Theme>[])
      : sx === undefined
        ? []
        : [sx];
    return (
      <SvgIcon
        component={Glyph}
        inheritViewBox
        sx={
          [
            filled === true ? { "&&.lucide": { fill: "currentColor" } } : false,
            ...incoming,
          ] as SxProps<Theme>
        }
        {...props}
      />
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export const ArrowDownIcon = icon(ArrowDown, "ArrowDownIcon");
export const ArrowLeftIcon = icon(ArrowLeft, "ArrowLeftIcon");
export const ArrowRightIcon = icon(ArrowRight, "ArrowRightIcon");
export const ArrowUpIcon = icon(ArrowUp, "ArrowUpIcon");
export const BanIcon = icon(Ban, "BanIcon");
export const BellIcon = icon(Bell, "BellIcon");
export const BookOpenIcon = icon(BookOpen, "BookOpenIcon");
export const BoxIcon = icon(Box, "BoxIcon");
export const CalendarDaysIcon = icon(CalendarDays, "CalendarDaysIcon");
export const CameraIcon = icon(Camera, "CameraIcon");
export const ChartLineIcon = icon(ChartLine, "ChartLineIcon");
export const CheckIcon = icon(Check, "CheckIcon");
export const ChevronDownIcon = icon(ChevronDown, "ChevronDownIcon");
export const ChevronLeftIcon = icon(ChevronLeft, "ChevronLeftIcon");
export const ChevronRightIcon = icon(ChevronRight, "ChevronRightIcon");
export const ChevronUpIcon = icon(ChevronUp, "ChevronUpIcon");
export const ChevronsDownUpIcon = icon(ChevronsDownUp, "ChevronsDownUpIcon");
export const ChevronsUpDownIcon = icon(ChevronsUpDown, "ChevronsUpDownIcon");
export const CircleAlertIcon = icon(CircleAlert, "CircleAlertIcon");
export const CircleCheckIcon = icon(CircleCheck, "CircleCheckIcon");
export const CircleHelpIcon = icon(CircleHelp, "CircleHelpIcon");
export const CircleMinusIcon = icon(CircleMinus, "CircleMinusIcon");
export const ClipboardListIcon = icon(ClipboardList, "ClipboardListIcon");
export const Columns3Icon = icon(Columns3, "Columns3Icon");
export const CopyIcon = icon(Copy, "CopyIcon");
export const DownloadIcon = icon(Download, "DownloadIcon");
// DL-02 — the download pill while "Pause all" holds the queue.
export const PauseIcon = icon(Pause, "PauseIcon");
export const EllipsisVerticalIcon = icon(
  EllipsisVertical,
  "EllipsisVerticalIcon",
);
export const ExternalLinkIcon = icon(ExternalLink, "ExternalLinkIcon");
export const EyeIcon = icon(Eye, "EyeIcon");
export const EyeOffIcon = icon(EyeOff, "EyeOffIcon");
export const FileTextIcon = icon(FileText, "FileTextIcon");
export const FileUpIcon = icon(FileUp, "FileUpIcon");
export const FolderIcon = icon(Folder, "FolderIcon");
export const GripVerticalIcon = icon(GripVertical, "GripVerticalIcon");
export const HistoryIcon = icon(History, "HistoryIcon");
export const HouseIcon = icon(House, "HouseIcon");
export const InboxIcon = icon(Inbox, "InboxIcon");
export const InfoIcon = icon(Info, "InfoIcon");
export const KeyRoundIcon = icon(KeyRound, "KeyRoundIcon");
export const LayoutGridIcon = icon(LayoutGrid, "LayoutGridIcon");
export const LinkIcon = icon(Link, "LinkIcon");
export const LocateFixedIcon = icon(LocateFixed, "LocateFixedIcon");
export const LockIcon = icon(Lock, "LockIcon");
export const LogOutIcon = icon(LogOut, "LogOutIcon");
export const MapPinIcon = icon(MapPin, "MapPinIcon");
export const MenuIcon = icon(Menu, "MenuIcon");
export const MonitorIcon = icon(Monitor, "MonitorIcon");
export const NotebookPenIcon = icon(NotebookPen, "NotebookPenIcon");
export const PencilIcon = icon(Pencil, "PencilIcon");
export const PentagonIcon = icon(Pentagon, "PentagonIcon");
export const PinIcon = icon(Pin, "PinIcon");
export const PlusIcon = icon(Plus, "PlusIcon");
export const Redo2Icon = icon(Redo2, "Redo2Icon");
export const RefreshCwIcon = icon(RefreshCw, "RefreshCwIcon");
export const RotateCcwIcon = icon(RotateCcw, "RotateCcwIcon");
export const Rows2Icon = icon(Rows2, "Rows2Icon");
export const Rows3Icon = icon(Rows3, "Rows3Icon");
export const SaveIcon = icon(Save, "SaveIcon");
export const ScanSearchIcon = icon(ScanSearch, "ScanSearchIcon");
export const SearchIcon = icon(Search, "SearchIcon");
export const GlobeIcon = icon(Globe, "GlobeIcon");
export const SettingsIcon = icon(Settings, "SettingsIcon");
export const ShieldCheckIcon = icon(ShieldCheck, "ShieldCheckIcon");
export const ShieldIcon = icon(Shield, "ShieldIcon");
export const SlidersHorizontalIcon = icon(
  SlidersHorizontal,
  "SlidersHorizontalIcon",
);
export const SlidersVerticalIcon = icon(SlidersVertical, "SlidersVerticalIcon");
export const SquareIcon = icon(Square, "SquareIcon");
export const StarIcon = icon(Star, "StarIcon");
export const Trash2Icon = icon(Trash2, "Trash2Icon");
export const TriangleAlertIcon = icon(TriangleAlert, "TriangleAlertIcon");
// TT-02 — weather standby. The ONE glyph that says "held by the weather"
// without saying "wind" (which on this platform means the turbine, not the
// hold). Pairs with WrenchIcon for technical standby.
export const UmbrellaIcon = icon(Umbrella, "UmbrellaIcon");
export const Undo2Icon = icon(Undo2, "Undo2Icon");
export const UnlinkIcon = icon(Unlink, "UnlinkIcon");
export const UserIcon = icon(User, "UserIcon");
export const UserPlusIcon = icon(UserPlus, "UserPlusIcon");
export const UsersIcon = icon(Users, "UsersIcon");
export const WindIcon = icon(Wind, "WindIcon");

// WS-ICON-01 — added for the main app's per-leaf navigation icons. Same D80
// contract as every glyph above: Lucide, 24 grid, fill none, currentColor.
export const BugIcon = icon(Bug, "BugIcon");
export const Building2Icon = icon(Building2, "Building2Icon");
export const LibraryIcon = icon(Library, "LibraryIcon");
export const ReceiptTextIcon = icon(ReceiptText, "ReceiptTextIcon");
export const ScrollTextIcon = icon(ScrollText, "ScrollTextIcon");
export const TimerIcon = icon(Timer, "TimerIcon");
export const WrenchIcon = icon(Wrench, "WrenchIcon");
export const XIcon = icon(X, "XIcon");
